import { useEffect, useState, useCallback } from 'react';
import { validateVipConversion, errorMessages } from '../../vip/validateVipConversion';
import { fetchVipData, convertVipCoins } from '../../vip/vipApi';

export function useVipModule({ pollMs = 60000, minRequired = 10, remoteId, onExternalRefresh }) {
  const [vipBalance, setVipBalance] = useState(0);
  const [level, setLevel] = useState(null);
  const [amount, setAmount] = useState('');
  const [status, setStatus] = useState('IDLE'); // IDLE|CONVERTING|SUCCESS|ERROR
  const [errorCode, setErrorCode] = useState(null);
  const [message, setMessage] = useState('');
  const [loadingVip, setLoadingVip] = useState(false);

  // Load VIP data
  const loadVipData = useCallback(async () => {
    if (!remoteId) return;
    setLoadingVip(true);
    try {
      const data = await fetchVipData(remoteId);
      setVipBalance(data.vip_coin);
      setLevel(data.vip_level);
    } catch (e) {
      // optionally expose an error state
    } finally {
      setLoadingVip(false);
    }
  }, [remoteId]);

  useEffect(() => {
    let active = true;
    async function initial() { if (active) await loadVipData(); }
    if (remoteId) {
      initial();
      const id = setInterval(() => { if (active) loadVipData(); }, pollMs);
      return () => { active = false; clearInterval(id); };
    }
  }, [remoteId, pollMs, loadVipData]);

  const onAmountChange = useCallback((v) => {
    setAmount(v);
    setErrorCode(null);
    setStatus('IDLE');
  }, []);

  const onConvert = useCallback(async () => {
    const num = Number(amount);
    const validation = validateVipConversion({ amount: num, minRequired, balance: vipBalance });
    if (!validation.valid) {
      setErrorCode(validation.code);
      setStatus('ERROR');
      return;
    }
    setStatus('CONVERTING');
    try {
      const result = await convertVipCoins(remoteId, num);
      setMessage(`Converted ${result.converted_vip} VP → ${result.credited_amount}`);
      setVipBalance(result.remaining_vip);
      setStatus('SUCCESS');
      setAmount('');
      setErrorCode(null);
      // Force a fresh reload from server to ensure authoritative balance
      await loadVipData();
      if (typeof onExternalRefresh === 'function') {
        try { await onExternalRefresh(); } catch (_) {}
      }
    } catch (e) {
      setMessage(e.message || 'Conversion failed');
      setErrorCode(e.code || 'API_ERROR');
      setStatus('ERROR');
    }
  }, [amount, minRequired, remoteId, vipBalance, loadVipData]);

  const resetFeedback = useCallback(async () => {
    await loadVipData();
    if (typeof onExternalRefresh === 'function') {
      try { await onExternalRefresh(); } catch (_) {}
    }
    setStatus('IDLE');
    setMessage('');
    setErrorCode(null);
  }, [loadVipData, onExternalRefresh]);

  const canSubmit = (() => {
    if (!amount) return false;
    const num = Number(amount);
    const validation = validateVipConversion({ amount: num, minRequired, balance: vipBalance });
    return validation.valid;
  })();

  const errors = errorMessages(minRequired);
  const errorText = errorCode ? errors[errorCode] || 'Error' : '';
  const converting = status === 'CONVERTING';

  return {
    vipBalance,
    level,
    amount,
    status,
    errorCode,
    errorText,
    message,
    converting,
    onAmountChange,
    onConvert,
    resetFeedback,
    canSubmit,
    loadingVip,
    minRequired
  };
}

export default useVipModule;
